<!-- Order Details / Dispute Details -->
<div class="order-details-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <button class="btn btn-outline-secondary btn-sm" (click)="router.navigateByUrl('/admin/dispute_resolution')" style="margin-right: 8px;">
          <i class="fas fa-arrow-left"></i>
          Back
        </button>
        <div class="header-icon">
          <i class="fas fa-file-alt"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Order Details</h1>
          <p class="page-description">View dispute and order information</p>
        </div>
      </div>
      <div class="header-actions">
        @if (disputeDetails?.status == 'pending') {
          <button class="btn btn-primary" (click)="markAsResolved(disputeDetails.id)">
            <i class="fas fa-check-circle"></i>
            Mark as Resolved
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Content Grid -->
  <div class="row g-4">
    <!-- Items Column -->
    <div class="col-lg-5">
      <div class="data-panel">
        <div class="panel-header">
          <div class="panel-title">
            <div class="title-icon">
              <i class="fas fa-shopping-bag"></i>
            </div>
            <div>
              <div class="title-text">Order Items</div>
              <div class="title-subtitle">Items included in this order</div>
            </div>
          </div>
        </div>

        <div class="panel-body" style="padding: 16px 20px;">
          @for (item of disputeDetails?.order.items; track $index) {
            <div style="display: flex; align-items: center; gap: 14px; padding: 12px; background: var(--bg-muted); border-radius: var(--radius-md); margin-bottom: 10px; border: 1px solid var(--border-subtle);">
              <img [src]="item?.inventory?.main_image || 'amala.png'"
                   style="width: 56px; height: 56px; border-radius: var(--radius-md); object-fit: cover;"
                   alt="">
              <div>
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary);">{{ item?.inventory?.name }}</div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">
                  {{ item?.quantity }}
                  @if (item.quantity > 1) {units} @else {Unit}
                </div>
              </div>
            </div>
          } @empty {
            <div class="empty-state" style="padding: 40px 20px;">
              <div class="empty-icon">
                <i class="fas fa-box-open fa-2x"></i>
              </div>
              <div class="empty-content">
                <p>No items to display</p>
              </div>
            </div>
          }

          <!-- Order Amount -->
          <div style="margin-top: 20px; padding: 16px; background: var(--accent-subtle); border-radius: var(--radius-md); border: 1px solid var(--accent-border);">
            <div style="font-size: 12px; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">Order Amount</div>
            <div style="font-size: 22px; font-weight: 700; color: var(--accent);">{{ disputeDetails?.order?.amount | currency: "&#8358;" }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Details Column -->
    <div class="col-lg-7">
      <div class="data-panel">
        <div class="panel-header">
          <div class="panel-title">
            <div class="title-icon">
              <i class="fas fa-info-circle"></i>
            </div>
            <div>
              <div class="title-text">Order Information</div>
              <div class="title-subtitle">Details about the order and dispute</div>
            </div>
          </div>
        </div>

        <div class="panel-body" style="padding: 20px;">
          <!-- Order ID -->
          <div style="margin-bottom: 20px;">
            <div style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">Order ID</div>
            <div style="font-size: 14px; font-weight: 500; color: var(--text-primary);">
              <code style="background: var(--bg-muted); padding: 4px 8px; border-radius: var(--radius-sm);">{{ disputeDetails?.order?.uid }}</code>
            </div>
          </div>

          <!-- Dispute Reason -->
          <div style="margin-bottom: 20px;">
            <div style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">Dispute</div>
            <div style="font-size: 14px; color: var(--text-primary); background: var(--danger-bg); padding: 12px; border-radius: var(--radius-md); border: 1px solid var(--danger-border);">
              {{ disputeDetails?.reason }}
            </div>
          </div>

          <!-- Waiter Info -->
          <div style="margin-bottom: 20px;">
            <div style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">Waiter in Charge</div>
            <div class="d-flex align-items-center gap-3" style="padding: 10px; background: var(--bg-muted); border-radius: var(--radius-md);">
              <div style="width: 32px; height: 32px; border-radius: 50%; background: var(--info-bg); color: var(--info-text); display: flex; align-items: center; justify-content: center; font-size: 13px;">
                <i class="fas fa-user-tie"></i>
              </div>
              <span style="font-size: 13px; font-weight: 500; color: var(--text-primary);">{{ disputeDetails?.order?.waiter?.name }}</span>
            </div>
          </div>

          <!-- Customer Details -->
          <div>
            <div style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Customer Details</div>
            <div style="padding: 14px; background: var(--bg-muted); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
              <div class="d-flex align-items-center gap-3 mb-3">
                <div style="width: 40px; height: 40px; border-radius: 50%; background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 15px;">
                  <i class="fas fa-user"></i>
                </div>
                <div>
                  <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">{{ disputeDetails?.order?.user?.name }}</div>
                </div>
              </div>
              <div style="display: flex; flex-direction: column; gap: 6px; padding-left: 52px;">
                <div class="d-flex align-items-center gap-2" style="font-size: 13px; color: var(--text-secondary);">
                  <i class="fas fa-phone" style="width: 14px; color: var(--text-tertiary);"></i>
                  {{ disputeDetails?.order?.user?.phone }}
                </div>
                <div class="d-flex align-items-center gap-2" style="font-size: 13px; color: var(--text-secondary);">
                  <i class="fas fa-envelope" style="width: 14px; color: var(--text-tertiary);"></i>
                  {{ disputeDetails?.order?.user?.email }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
